import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const RevenueMetrics = ({ dateRange, tenantId }) => {
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    recurringRevenue: 0,
    setupFees: 0,
    averageValue: 0,
    growthRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      loadRevenueMetrics();
    }
  }, [tenantId, dateRange]);

  const loadRevenueMetrics = async () => {
    setLoading(true);
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      // Load billing records
      const { data: billingData, error } = await supabase?.from('billing_records')?.select('*')?.eq('tenant_id', tenantId)?.gte('billing_date', startDate?.toISOString()?.split('T')?.[0])?.eq('payment_status', 'completed');

      if (error) throw error;

      // Calculate metrics
      const totalRevenue = billingData?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;
      const recurringRevenue = billingData?.filter(b => !b?.is_prorated)?.reduce(
        (sum, b) => sum + parseFloat(b?.amount || 0), 0
      ) || 0;
      const setupFees = totalRevenue - recurringRevenue;
      const averageValue = billingData?.length > 0 ? totalRevenue / billingData?.length : 0;

      // Calculate growth rate (compare with previous period)
      const previousPeriodStart = new Date(startDate);
      previousPeriodStart?.setDate(previousPeriodStart?.getDate() - daysAgo);
      
      const { data: previousData } = await supabase?.from('billing_records')?.select('total_amount')?.eq('tenant_id', tenantId)?.gte('billing_date', previousPeriodStart?.toISOString()?.split('T')?.[0])?.lt('billing_date', startDate?.toISOString()?.split('T')?.[0])?.eq('payment_status', 'completed');

      const previousRevenue = previousData?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;
      const growthRate = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      setMetrics({
        totalRevenue,
        recurringRevenue,
        setupFees,
        averageValue,
        growthRate
      });
    } catch (err) {
      console.error('Failed to load revenue metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <h2 className="text-xl font-heading font-bold text-foreground mb-6 flex items-center gap-2">
        <Icon name="TrendingUp" size={24} color="var(--color-accent)" />
        Revenue Metrics
      </h2>
      <div className="space-y-6">
        {/* Total Revenue */}
        <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Revenue</span>
            <Icon name="DollarSign" size={20} color="var(--color-primary)" />
          </div>
          <p className="text-3xl font-bold text-foreground">
            ${metrics?.totalRevenue?.toFixed(2)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <Icon
              name={metrics?.growthRate >= 0 ? 'TrendingUp' : 'TrendingDown'}
              size={16}
              color={metrics?.growthRate >= 0 ? 'var(--color-success)' : 'var(--color-destructive)'}
            />
            <span
              className={`text-sm font-semibold ${
                metrics?.growthRate >= 0 ? 'text-success' : 'text-destructive'
              }`}
            >
              {metrics?.growthRate >= 0 ? '+' : ''}{metrics?.growthRate?.toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground ml-1">vs previous period</span>
          </div>
        </div>

        {/* Recurring Revenue */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Recurring Revenue</span>
            <Icon name="Repeat" size={20} color="var(--color-success)" />
          </div>
          <p className="text-2xl font-bold text-foreground">
            ${metrics?.recurringRevenue?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {metrics?.totalRevenue > 0
              ? ((metrics?.recurringRevenue / metrics?.totalRevenue) * 100)?.toFixed(1)
              : 0}% of total
          </p>
        </div>

        {/* Setup Fees */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Setup Fees</span>
            <Icon name="CreditCard" size={20} color="var(--color-accent)" />
          </div>
          <p className="text-2xl font-bold text-foreground">
            ${metrics?.setupFees?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {metrics?.totalRevenue > 0
              ? ((metrics?.setupFees / metrics?.totalRevenue) * 100)?.toFixed(1)
              : 0}% of total
          </p>
        </div>

        {/* Average Subscription Value */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Avg. Subscription Value</span>
            <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          </div>
          <p className="text-2xl font-bold text-foreground">
            ${metrics?.averageValue?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
        </div>
      </div>
    </div>
  );
};

export default RevenueMetrics;